import React, { useState } from 'react';
import { Money, Receipt, CheckCircle } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND, formatDate, toYmd } from '_helpers';
import { PageHeader, Bezel, Table, StatCard, Button, Modal, Input, MotionReveal } from '_ui';
import { InvoiceDetail } from '_components/InvoiceDetail';

function itemsSummary(items = []) {
    if (!items.length) return '—';
    const first = `${items[0].name} ×${items[0].quantity}`;
    return items.length > 1 ? `${first} +${items.length - 1}` : first;
}

export function TienMat() {
    const [date, setDate] = useState(toYmd());
    const { data, loading, refetch } = useFetch(`/api/cash?date=${date}`);
    const api = useApi();
    const alert = useAlertActions();
    const [confirming, setConfirming] = useState(false);
    const [approving, setApproving] = useState(false);
    const [selected, setSelected] = useState(null);

    const count = data?.count || 0;
    const total = data?.total || 0;
    const invoices = data?.invoices || [];

    async function approve() {
        setApproving(true);
        const res = await api.patch('/api/cash/approve', { date });
        setApproving(false);
        setConfirming(false);
        if (res.ok) { alert.success('Đã duyệt tiền mặt', `${res.data.approved} hoá đơn · ${formatVND(res.data.total)}`); refetch(); }
        else alert.error('Không thể duyệt', res.error);
    }

    const columns = [
        { key: 'createdAt', header: 'Giờ tạo', render: r => <span className="tnum text-espresso-soft">{formatDate(r.createdAt, true)}</span> },
        { key: 'customer', header: 'Khách', render: r => r.customerName || <span className="text-espresso-soft/50">—</span> },
        { key: 'items', header: 'Dịch vụ', render: r => <span className="text-espresso-soft">{itemsSummary(r.items)}</span> },
        { key: 'total', header: 'Số tiền', align: 'right', render: r => <span className="tnum font-medium">{formatVND(r.totalPrice)}</span> },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Quản lý"
                title="Tiền mặt"
                description="Các hoá đơn tiền mặt chờ duyệt trong ngày. Duyệt để xác nhận đã nhận đủ tiền mặt — hoa hồng được tính sau khi duyệt."
                actions={
                    <label className="flex items-center gap-2">
                        <span className="text-sm text-espresso-soft">Ngày</span>
                        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-auto py-2" />
                    </label>
                }
            />

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Hoá đơn chờ duyệt" value={count} icon={Receipt} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Tổng tiền mặt" value={formatVND(total)} icon={Money} accent="brass" /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <div className="flex items-center justify-between gap-4 border-b border-espresso/[0.07] px-6 py-5">
                    <h3 className="font-serif text-xl text-espresso">Chờ duyệt</h3>
                    <Button icon={CheckCircle} onClick={() => setConfirming(true)} disabled={count === 0}>
                        Duyệt tiền mặt
                    </Button>
                </div>
                <Table columns={columns} rows={invoices} loading={loading} onRowClick={setSelected} empty="Không có hoá đơn tiền mặt nào chờ duyệt trong ngày." />
            </Bezel>

            <InvoiceDetail invoice={selected} onClose={() => setSelected(null)} />

            <Modal open={confirming} onClose={() => setConfirming(false)} eyebrow="Xác nhận" title="Duyệt tiền mặt trong ngày?"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setConfirming(false)}>Huỷ</Button>
                        <Button icon={CheckCircle} onClick={approve} loading={approving}>Duyệt {count} hoá đơn</Button>
                    </>
                }>
                <p className="text-espresso-soft">
                    Bạn xác nhận đã nhận đủ <span className="font-semibold text-espresso">{formatVND(total)}</span> tiền mặt từ{' '}
                    <span className="font-semibold text-espresso">{count}</span> hoá đơn ngày {formatDate(date)}?
                    Sau khi duyệt, các hoá đơn này được tính là đã thanh toán.
                </p>
            </Modal>
        </div>
    );
}
